// configActions.js
'use strict';
import { SET_CONFIG_DATA } from '../ActionTypes.js';
import { APP_BUNDLE_IDS } from '../RemoteConfigKeys.js';

import {
  init,
  getFeature,
  refresh,
  setAttribute,
} from '../resource/growthBook.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import env, { updateEnv } from '../resource/env.js';
import { setAppBundleIds } from '../resource/getUserAgent.js';
import { GrowthBookInitStatus } from '../resource/configConstants.js';
import { remoteConfig as remoteConfigDebug } from '../resource/debug.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getConfigData from '../selector/getConfigData.js';

const growthBookLog = remoteConfigDebug.extend('log:configActions');
const growthBookError = remoteConfigDebug.extend('error:configActions');

/**
 * Merge config from all sources and dispatch result.
 * @kind action
 * @return {Promise} Action promise.
 */
const mergeConfigResult = () => async (dispatch, getState) => {
  const environment = env || EMPTY_OBJECT;
  const remoteConfig = getState().get('remoteConfig')?.toJS() || EMPTY_OBJECT;
  const growthbook = (await getFeature()) || EMPTY_OBJECT; // growthbook config is empty before init.
  const webview = window.WebApp?.remoteConfig || EMPTY_OBJECT;
  const mdm = window.Mdm?.remoteConfig || EMPTY_OBJECT;

  const result = {
    ...environment,
    ...remoteConfig,
    ...growthbook,
    ...webview,
    ...mdm,
  };

  [
    { key: 'env', data: environment },
    { key: 'growthbook', data: growthbook },
    { key: 'webview', data: webview },
    { key: 'mdm', data: mdm },
    { key: 'result', data: result },
  ].forEach(({ key, data }) => {
    dispatch({
      type: SET_CONFIG_DATA,
      payload: { selectPath: [key], data },
    });
  });

  updateEnv(result, true);

  const appBundleIds = getRemoteConfigData(getState(), APP_BUNDLE_IDS);
  if (appBundleIds?.length) {
    setAppBundleIds({ ids: appBundleIds });
  }
};

/**
 * Refresh GrowthBook features and update config
 * @kind action
 * @return {Promise} Action promise.
 */
const refreshGrowthBookConfig = () => async dispatch => {
  await refresh();
  await dispatch(mergeConfigResult());
};

/**
 * Init growth book config
 * @kind action
 * @param {any} {attribute} - action param.
 * @return {Promise} Action promise.
 */
const initGrowthBookConfig =
  ({ attribute } = {}) =>
  async dispatch => {
    dispatch({
      type: SET_CONFIG_DATA,
      payload: {
        selectPath: ['status', 'initStatus'],
        data: GrowthBookInitStatus.INITIALIZING,
      },
    });
    await dispatch(mergeConfigResult());

    try {
      const gb = await init();
      await setAttribute({ attribute });

      window._growthbook = gb;

      await dispatch(mergeConfigResult());
      dispatch({
        type: SET_CONFIG_DATA,
        payload: {
          selectPath: ['status', 'initStatus'],
          data: GrowthBookInitStatus.SUCCESS,
        },
      });
    } catch (error) {
      // Keep the baseline config.result merged before init so consumers
      // still see env / remoteConfig / webview / mdm values; only mark
      // init as FAILED.
      dispatch({
        type: SET_CONFIG_DATA,
        payload: {
          selectPath: ['status', 'initStatus'],
          data: GrowthBookInitStatus.FAILED,
        },
      });
    }
  };

/**
 * Update growth book config with new attribute
 * @kind action
 * @param {object} {attribute} - new attribute.
 * @return {Promise} Action promise.
 */
const updateGrowthBookAttribute =
  ({ attribute } = {}) =>
  async (dispatch, getState) => {
    const log = growthBookLog.extend('updateGrowthBookAttribute');
    const errorLog = growthBookError.extend('updateGrowthBookAttribute');

    log('execute update growthbook config', { attribute, now: Date.now() });

    const initStatus = getConfigData(getState(), ['status', 'initStatus']);

    if (initStatus === GrowthBookInitStatus.SUCCESS) {
      log('set user attributes and update features', {
        attribute,
        now: Date.now(),
      });

      dispatch({
        type: SET_CONFIG_DATA,
        payload: {
          selectPath: ['status', 'initStatus'],
          data: GrowthBookInitStatus.UPDATING,
        },
      });

      try {
        await setAttribute({ attribute });
        await dispatch(refreshGrowthBookConfig());
      } catch (error) {
        errorLog(
          'error occurred while set user attributes and update features',
          {
            attribute,
            now: Date.now(),
            error,
          }
        );
      }

      dispatch({
        type: SET_CONFIG_DATA,
        payload: {
          selectPath: ['status', 'initStatus'],
          data: GrowthBookInitStatus.SUCCESS,
        },
      });

      log('finish set user attributes and update features', {
        attribute,
        now: Date.now(),
      });
    }

    log('done update growthbook config', { attribute, now: Date.now() });
  };

export {
  initGrowthBookConfig,
  updateGrowthBookAttribute,
  refreshGrowthBookConfig,
};
